import { useState } from "react";
import { Building2, CheckCircle2, Clock, AlertTriangle, ExternalLink, Send, RefreshCw, Loader2 } from "lucide-react";
import { SISTEMAS } from "../constants";
import { resumoAtivacao, rotuloOrigem, corOrigem } from "../lib/adesao";
import { fmtShort } from "../utils";
import { consultarCliente, cadastrarCliente, respostaParaSync } from "../lib/clienteModulo";

// ═══════════════════════════════════════════════════════════════
// ATIVAÇÃO NOS MÓDULOS — o cliente já está de pé lá dentro?
// ═══════════════════════════════════════════════════════════════
//
// A adesão é uma promessa; ativo no módulo é o fato. Entre os dois some muita
// receita: contrato assinado que nunca virou acesso, rastreador vendido que
// nunca foi instalado. Este bloco existe para essa distância ser visível.
//
// Ele ESPELHA — não é dono de nada. O id do cliente, o plano e a fatura vivem
// no módulo; aqui só aparece se chegou lá e quando.

const ESTADOS = {
  ativo: { label: "Ativo", cor: "#059669", Icone: CheckCircle2 },
  enviado: { label: "Enviado, aguardando", cor: "#f59e0b", Icone: Clock },
  erro: { label: "Falhou", cor: "#dc2626", Icone: AlertTriangle },
  nao_enviado: { label: "Ainda não enviado", cor: "#94a3b8", Icone: Building2 },
};

export default function AtivacaoNosModulos({ cliente, onAbrirAdesao, setClients }) {
  const [ocupado, setOcupado] = useState("");
  const [aviso, setAviso] = useState({});
  if (!cliente) return null;
  const r = resumoAtivacao(cliente);

  // Manda o cadastro para o módulo, ou pergunta como ele está. O que volta é
  // gravado como veio — o CRM não interpreta o estado do módulo, espelha.
  const falarCom = async (sistema, acao) => {
    setOcupado(sistema);
    setAviso((p) => ({ ...p, [sistema]: "" }));
    const resposta = acao === "cadastrar"
      ? await cadastrarCliente(sistema, cliente)
      : await consultarCliente(sistema, cliente.cnpj);
    setOcupado("");
    const sync = respostaParaSync(sistema, resposta);
    if (setClients) {
      setClients((prev) => prev.map((c) => (c.id !== cliente.id ? c : {
        ...c,
        integracaoSync: { ...(c.integracaoSync || {}), ...sync },
        historico: [...(Array.isArray(c.historico) ? c.historico : []), {
          ts: new Date().toISOString(),
          acao: sync[sistema].ok ? "ativacao" : "ativacao_falhou",
          detalhe: sync[sistema].ok
            ? `Cliente ${acao === "cadastrar" ? "criado" : "confirmado"} no módulo ${sistema} (id ${sync[sistema].client_id}).`
            : `Módulo ${sistema}: ${sync[sistema].erro}`,
        }],
      })));
    }
    if (!sync[sistema].ok) setAviso((p) => ({ ...p, [sistema]: sync[sistema].erro }));
  };

  if (r.contratados === 0) {
    return (
      <div style={{ padding: "12px 14px", borderRadius: 9, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
          Nenhum módulo contratado registrado ainda.
          {onAbrirAdesao && <> Use <strong>Registrar adesão</strong> para dizer o que ele contratou e abrir a ativação.</>}
        </div>
        {onAbrirAdesao && (
          <button onClick={onAbrirAdesao} style={{ marginTop: 9, padding: "7px 14px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
            Registrar adesão
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: "12px 14px", borderRadius: 9, background: "#fff", border: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
          Ativação nos módulos — {r.ativos} de {r.contratados}
        </span>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {cliente.origem && (
            <span style={{ fontSize: 9.5, fontWeight: 700, color: corOrigem(cliente.origem), background: `${corOrigem(cliente.origem)}14`, padding: "2px 8px", borderRadius: 10 }}>
              {rotuloOrigem(cliente.origem)}
            </span>
          )}
          {r.completo && (
            <span style={{ fontSize: 9.5, fontWeight: 700, color: "#059669", background: "#f0fdf4", padding: "2px 8px", borderRadius: 10 }}>
              OPERANDO
            </span>
          )}
        </span>
      </div>

      {r.estados.map((e) => {
        const s = SISTEMAS.find((x) => x.id === e.sistema);
        const est = ESTADOS[e.estado] || ESTADOS.nao_enviado;
        const Icone = est.Icone;
        const dados = cliente.adesao?.[e.sistema] || {};
        return (
          <div key={e.sistema} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0", borderTop: "1px solid #f8fafc" }}>
            <Icone size={14} color={est.cor} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
                {s?.label || e.sistema}
                <span style={{ fontSize: 10.5, fontWeight: 600, color: est.cor, marginLeft: 7 }}>{est.label}</span>
              </div>
              <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 1 }}>
                {dados.veiculos ? `${dados.veiculos} veículo(s)` : ""}
                {dados.plano ? ` · plano ${dados.plano}` : ""}
                {dados.limiteMensal ? ` · limite R$ ${dados.limiteMensal}` : ""}
                {e.estado === "ativo" && e.id ? ` · id ${e.id}` : ""}
                {e.ts ? ` · ${fmtShort(String(e.ts).slice(0, 10))}` : ""}
              </div>
              {e.estado === "erro" && (
                <div style={{ fontSize: 10.5, color: "#b91c1c", marginTop: 3 }}>{e.erro}</div>
              )}
              {e.estado === "nao_enviado" && (
                <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 3, lineHeight: 1.4 }}>
                  O CRM ainda não falou com este módulo. Se o cadastro já foi feito por lá, use
                  <strong> Conferir lá</strong> para trazer o estado; se não foi, <strong>Criar no módulo</strong>
                  evita redigitar tudo de novo.
                </div>
              )}
              {(e.pendencias || []).length > 0 && (
                <div style={{ fontSize: 10.5, color: "#b45309", marginTop: 3 }}>
                  Falta lá dentro: {e.pendencias.join(", ")}.
                </div>
              )}
              {aviso[e.sistema] && (
                <div style={{ fontSize: 10.5, color: "#b91c1c", marginTop: 3 }}>{aviso[e.sistema]}</div>
              )}
              {setClients && (
                <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap", flexDirection: e.estado === "nao_enviado" ? "row-reverse" : "row", justifyContent: "flex-end" }}>
                  {e.estado !== "ativo" && (
                    <button
                      onClick={() => falarCom(e.sistema, "cadastrar")} disabled={!!ocupado}
                      style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 10.5, fontWeight: 700, cursor: ocupado ? "wait" : "pointer" }}
                    >
                      {ocupado === e.sistema ? <Loader2 size={10} className="girar" /> : <Send size={10} />} Criar no módulo
                    </button>
                  )}
                  <button
                    onClick={() => falarCom(e.sistema, "consultar")} disabled={!!ocupado}
                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 10.5, fontWeight: 600, cursor: ocupado ? "wait" : "pointer" }}
                  >
                    <RefreshCw size={10} /> Conferir lá
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9.5, color: "#94a3b8", marginTop: 9, paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
        <ExternalLink size={10} />
        Plano, fatura e veículos ficam dentro de cada módulo. Aqui é só o espelho — e criar pelo botão é
        atalho para não redigitar, nunca obrigação: dá para cadastrar direto no módulo e depois conferir aqui.
      </div>
      <style>{`.girar { animation: girar 1s linear infinite } @keyframes girar { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
